import React from "react";
import AlertIcon from "@mui/icons-material/WarningAmberOutlined";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import PeopleIcon from "@mui/icons-material/PeopleOutline";
import EventSeatIcon from "@mui/icons-material/EventSeatOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import InboxIcon from "@mui/icons-material/InboxOutlined";
import ClipboardIcon from "@mui/icons-material/AssignmentOutlined";
import CertIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ShieldIcon from "@mui/icons-material/ShieldOutlined";
import ClockIcon from "@mui/icons-material/AccessTime";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarMonthOutlined";
import AhaInventoryWidget from "../AhaInventoryWidget";
import { useReady } from "../admin/adminCharts";
import { ROUTES } from "../shared/classData";
import { ActionList, Bar, LoadList, daysUntil, fmtDate } from "../widgets/commonBodies";
import "./coordinatorWidgets.css";
import { CoiBody, ExpiringBody, GradingBody, RequestsBody, RosterBody } from "./dataWidgets";

const STATUS_LABELS = { ok: "On track", risk: "At risk", behind: "Behind" };

function AttentionBody({ ctx }) {
  return <ActionList actions={ctx.m.actions} />;
}

function WeekBody({ ctx }) {
  const { week, empty } = ctx.m;
  return (
    <div className="cdw-wk">
      {week.days.map((d) => (
        <div key={d.name} className={`cdw-day${d.today ? " today" : d.past ? " past" : ""}`}>
          <div className="cdw-dh"><small>{d.name}</small><b>{d.day}</b></div>
          <div className="cdw-ds">{d.sessions.map((s) => (
            <button type="button" key={s.id} className={`cdw-ss${s.live ? " live" : ""}`} onClick={() => ctx.navigate(ROUTES.courseHub(s.courseId))}>
              <span className="tm"><i style={{ background: s.color }}></i>{s.time}</span><b>{s.name}</b>{s.meta && <span className="m">{s.meta}</span>}
              {s.live && <span className="lv">In session</span>}</button>))}</div>
        </div>
      ))}
      {empty && <div className="cdw-empt"><div><CalendarTodayIcon sx={{ fontSize: 22, opacity: 0.4 }} /><p>Nothing scheduled this week</p><small>Sessions appear here once a cohort has a schedule.</small>
        <button type="button" className="wg-btn" onClick={() => ctx.navigate(ROUTES.manageCourses)}><AddIcon sx={{ fontSize: 16 }} />Schedule session</button></div></div>}
    </div>
  );
}

function CohortsBody({ ctx }) {
  const ready = useReady(300);
  const { cohorts, setup, empty } = ctx.m;
  if (empty) {
    return (
      <>
        <div className="cdw-sbar">{setup.map((s) => <i key={s.title} className={ready && s.state === "done" ? "on" : ""}></i>)}</div>
        <div className="cdw-setup" data-testid="coordinator-setup">{setup.map((s, i) => (
          <div key={s.title} className={`cdw-su ${s.state}`}>
            <span className="n">{s.state === "done" ? <CheckIcon sx={{ fontSize: 14 }} /> : i + 1}</span>
            <div><p>{s.title}</p><small>{s.detail}</small></div>
            {s.state === "next" ? <button type="button" className="wg-btn pri" onClick={s.onClick}>{s.cta}</button> : <span></span>}</div>))}</div>
      </>
    );
  }
  return (
    <table className="cdw-tb" data-testid="coordinator-cohorts"><thead><tr><th>Cohort</th><th className="hm">Lead instructor</th><th>Progress</th><th className="hm">Learners</th><th>Status</th></tr></thead>
      <tbody>{cohorts.map((c) => (
        <tr key={c.id} onClick={c.onClick}>
          <td><div className="cdw-cn"><i style={{ background: c.color }}></i><div>{c.name}{c.code && <small>{c.code}</small>}</div></div></td>
          <td className="hm">{c.instructor}</td>
          <td>{c.upcoming ? <span className="mo" style={{ opacity: 0.65 }}>Starts {c.startLabel}</span>
            : c.pct == null ? <span className="mo" style={{ opacity: 0.65 }}>—</span>
              : <div className="cdw-pg"><Bar pct={c.pct} /><span>{c.sessionLabel || `${c.pct}%`}</span></div>}</td>
          <td className="hm mo">{c.cap ? `${c.learners} of ${c.cap}` : c.learners}</td>
          <td><span className={`cdw-stp ${c.status}`}>{c.status === "soon" ? `Starts ${c.startLabel}` : STATUS_LABELS[c.status]}</span></td>
        </tr>))}</tbody></table>
  );
}

function SeatsBody({ ctx }) {
  const rows = ctx.raw.courses
    .filter((c) => c.publishStatus !== "DRAFT" && c.lifecycleStatus !== "COMPLETED" && c.date)
    .filter((c) => { const d = daysUntil(c.date, ctx.now); return d >= 0 && d <= 30; })
    .sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 6);
  if (!rows.length) return <p className="wg-empty">No classes scheduled in the next 30 days.</p>;
  return (
    <div className="wl">{rows.map((c, i) => {
      const total = Number(c.capacity) || 0;
      const filled = Number(c.studentCount) || 0;
      const full = total > 0 && filled >= total;
      const low = total > 0 && filled / total < 0.5;
      return (
        <div key={c.id} className="wr3">
          <div><b>{c.title}</b><small>{fmtDate(c.date, { weekday: "short", month: "short", day: "numeric" })}</small></div>
          <div className="pbl"><Bar pct={total ? filled / total * 100 : 0} delay={i * 70} color={full ? "var(--color-success)" : low ? "var(--color-warn)" : "var(--color-ink)"} />
            <span>{full ? `Full${c.waitlistCount ? ` · ${c.waitlistCount} waitlisted` : ""}` : total ? `${filled} of ${total} seats` : `${filled} enrolled`}</span></div>
          <button type="button" className="go" onClick={() => ctx.navigate(ROUTES.courseHub(c.id))}>Roster</button>
        </div>
      );
    })}</div>
  );
}

function LoadBody({ ctx }) {
  return <LoadList rows={ctx.m.load} emptyText="Add instructors to see their weekly hours." />;
}

/** Instructor certifications that are expired or expiring in 90 days, with the classes they are assigned to. */
function InstCredsBody({ ctx }) {
  const names = Object.fromEntries(ctx.raw.instructors.map((i) => [i.id || i.userId, i.fullName || i.username || "Instructor"]));
  const classesBy = (id) => ctx.raw.courses.filter((c) => (c.instructorId === id || c.leadInstructorId === id) && c.lifecycleStatus !== "COMPLETED" && c.publishStatus !== "DRAFT");
  const rows = ctx.raw.instructorCerts.filter((c) => c.expires && daysUntil(c.expires, ctx.now) <= 90)
    .map((c) => ({ ...c, days: daysUntil(c.expires, ctx.now) })).sort((a, b) => a.days - b.days).slice(0, 6);
  if (!rows.length) return <p className="wg-empty">No instructor credentials are expired or expiring soon.</p>;
  return (
    <div className="wl">{rows.map((c) => {
      const affected = classesBy(c.userId);
      const bad = c.days < 0;
      return (
        <div key={c.id} className="wr"><div><b>{names[c.userId] || "Instructor"}</b><small>{c.name} · {bad ? "Expired" : "Expires"} {fmtDate(c.expires)}</small>
          {affected.length > 0 && <small style={{ color: bad ? "var(--color-danger)" : "var(--color-warn)", opacity: 1 }}>Assigned to {affected.length} active class{affected.length === 1 ? "" : "es"}</small>}</div>
          <span className={`tag ${bad ? "bad" : "warn"}`}>{bad ? "Expired" : `${c.days} days`}</span></div>
      );
    })}</div>
  );
}

function AhaBody({ ctx }) {
  return <AhaInventoryWidget model={ctx.aha} />;
}

/** Coordinator widget library. `available` hides a widget for tenants it does not apply to. */
export const COORDINATOR_WIDGETS = {
  attention: { name: "Needs attention", group: "Overview", desc: "Expiring credentials, sign-offs waiting, course submissions and cohorts without an instructor.", Icon: AlertIcon, sizes: [4, 6, 12], count: (ctx) => ctx.m.actions.length || null, isEmpty: (ctx) => ctx.m.actions.length === 0, emptyText: "Nothing needs your attention.", Body: AttentionBody },
  week: { name: "This week", group: "Scheduling", desc: "Seven-day session schedule built from your classes; the session running now is highlighted.", Icon: CalendarIcon, sizes: [8, 12], meta: (ctx) => ctx.m.week.label, link: { label: "Full schedule", to: ROUTES.catalog }, Body: WeekBody },
  roster: { name: "Today's roster", group: "Scheduling", desc: "Check-ins for the session running now, with learners who have not checked in.", Icon: PeopleIcon, sizes: [4, 6], Body: RosterBody },
  seats: { name: "Seat fill", group: "Scheduling", desc: "Enrollment against capacity for classes in the next 30 days.", Icon: EventSeatIcon, sizes: [6, 8, 12], meta: "Next 30 days", Body: SeatsBody },
  cohorts: { name: (ctx) => (ctx.m.empty ? `Get ${ctx.m.tenantName} running` : "Cohorts"), title: "Cohorts", group: "Learners", desc: "Schedule progress and readiness for every running cohort; a setup checklist for new centers.", Icon: BarChartIcon, sizes: [8, 12], link: (ctx) => (ctx.m.empty ? null : { label: "All cohorts", to: ROUTES.catalog }), Body: CohortsBody },
  requests: { name: "Enrollment requests", group: "Learners", desc: "Pending enrollments to approve or decline.", Icon: InboxIcon, sizes: [4, 6, 8], add: 6, Body: RequestsBody },
  grading: { name: "Grading queue", group: "Learners", desc: "Skill sheets waiting on instructor sign-off, by instructor.", Icon: ClipboardIcon, sizes: [4, 6], Body: GradingBody },
  expiring: { name: "Learner certifications expiring", group: "Compliance", desc: "Learners whose certifications expire within 30 days.", Icon: CertIcon, sizes: [6, 8, 12], add: 8, Body: ExpiringBody },
  coi: { name: "Insurance (COI)", group: "Compliance", desc: "Latest certificate of insurance for each partner agency.", Icon: ShieldIcon, sizes: [4, 6], link: { label: "All certificates", to: "/agency" }, Body: CoiBody },
  load: { name: "Instructor load", group: "Staff", desc: "Scheduled hours per instructor against the 40-hour target.", Icon: ClockIcon, sizes: [4, 6], meta: "This week", Body: LoadBody },
  instcreds: { name: "Instructor credentials", group: "Staff", desc: "Instructor certifications that are expired or expiring, and the classes affected.", Icon: KeyIcon, sizes: [4, 6], Body: InstCredsBody },
  aha: { name: "AHA eCards remaining", group: "Compliance", desc: "AHA eCard inventory for centers that issue AHA cards.", Icon: CertIcon, sizes: [4, 6], available: (ctx) => Boolean(ctx.aha), Body: AhaBody },
};

export const COORDINATOR_DEFAULTS = [["week", 8], ["attention", 4], ["cohorts", 8], ["load", 4]];

